import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import produce from 'immer';
import { DEFAULT_PAGE_SIZE, VOICES } from '../../commons/constants/constants';
import { updateTourGuides } from '../../services/tour.service';
import { PaginationType } from '../../types/CommonTypes';
import { TourData } from '../../types/TourData';
import { TourGuideData } from '../../types/TourSoundData';
import { storeName as tourEntityStoreName } from '../entities/tour.entity';
import { storeName as tourGuideEntityStoreName } from '../entities/tourGuide.entity';
import { RootModel } from '../models';
import { storeName as tourModelStoreName } from './tour.model';

export const storeName = 'mapTourGuide';

const initialPaginationValue: PaginationType = {
  pageable: null,
  sort: null,
  totalPages: 0,
  totalElements: 0,
  last: false,
  first: true,
  numberOfElements: 0,
  size: DEFAULT_PAGE_SIZE,
  number: 0,
  empty: null,
};

export interface initialValuesType {
  tourId: null;
  selectedTourGuides: { languageCode: string; tourGuideId: number | undefined }[];
  unselectedTourGuideIds: number[];
  unselectedTourGuidePagination: PaginationType | null;
  newTourGuideIds: number[];
}

export const initialValues: initialValuesType = {
  tourId: null,
  selectedTourGuides: [],
  unselectedTourGuideIds: [],
  unselectedTourGuidePagination: initialPaginationValue,
  newTourGuideIds: [],
};

const mapTourGuide: any = createModel<RootModel>()({
  state: initialValues, // initial state
  reducers: {
    reset: () => initialValues,
    setTourId: produce((state, payload) => {
      state.tourId = payload;
    }),
    setSelectedTourGuides: produce((state, tourGuides) => {
      state.selectedTourGuides = tourGuides;
    }),
    setUnselectedTourGuideData: produce((state, { ids, pagination }) => {
      state.unselectedTourGuideIds = ids || [];
      state.unselectedTourGuidePagination = pagination || initialPaginationValue;
    }),
    addNewTourGuide: produce((state, payload: TourGuideData) => {
      const { selectedTourGuides } = state;
      const newSelectedTourGuides = selectedTourGuides.map((item) => {
        if (payload && payload.languageCode === item.languageCode) {
          return {
            languageCode: item.languageCode,
            tourGuideId: payload.id,
          };
        }

        return item;
      });
      state.selectedTourGuides = newSelectedTourGuides;
    }),
    removeTourGuide: produce((state, id) => {
      const { selectedTourGuides } = state;
      const newSelectedTourGuides = selectedTourGuides.map((item) => {
        if (id === item.tourGuideId) {
          return {
            languageCode: item.languageCode,
            tourGuideId: undefined,
          };
        }
        return item;
      });
      state.selectedTourGuides = newSelectedTourGuides;
    }),
  },
  effects: (dispatch: RematchDispatch<typeof mapTourGuide>) => ({
    async setEditTourGuide(id, state) {
      if (!id) {
        this.reset();
      } else {
        this.setTourId(id);
        const tour: TourData = await dispatch[tourEntityStoreName].loadById(id);
        if (tour && tour.tourGuides) {
          const selectedTourGuides = Object.values(VOICES).map((voice) => {
            const tourGuide = tour.tourGuides.find((item) => item.languageCode === voice.code);
            return {
              languageCode: voice.code,
              tourGuideId: tourGuide ? tourGuide.id : undefined,
            };
          });
          this.setSelectedTourGuides(selectedTourGuides);
        }
        dispatch[tourModelStoreName].setEditTourGuide(tour);
      }
    },
    async loadUnselectedTourGuides(params, state) {
      const data = await dispatch[tourGuideEntityStoreName].load(params);
      const ids = data.content.map((scene) => scene.id);
      const pagination = (({ content, ...otherProps }) => otherProps)(data);
      this.setUnselectedTourGuideData({ ids, pagination });
    },
    async save(payload, state) {
      const { tourId, selectedTourGuides } = state[storeName];
      const editTourGuides = selectedTourGuides
        .filter((item) => item.tourGuideId)
        .map((item) => {
          return { id: item.tourGuideId };
        });
      try {
        await updateTourGuides(tourId, { id: tourId, tourGuides: editTourGuides });
        message.success('Success to save tour');
        dispatch[tourModelStoreName].reloadList();
      } catch (e) {
        console.error(e);
        message.error('Fail to save tour');
      }
    },
  }),
});

export default mapTourGuide;
